import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination,
  Chip, TextField, InputAdornment, CircularProgress,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Collapse, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useTenant } from '../../context/TenantContext';
import GhlSyncDialog from './GhlSyncDialog';
import { environment } from '../../env/environment';
import logger from '../../utils/logger';
import { downloadPatientPdf } from '../../services/patientPdfService';

const FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'intake-pending', label: 'Intake Pending', color: '#f59e0b', bg: '#fef3c7' },
  { key: 'intake-complete', label: 'Intake Complete', color: '#16a34a', bg: '#dcfce7' },
  { key: 'followup-sent', label: 'F/U Sent', color: '#d97706', bg: '#fef3c7' },
  { key: 'followup-pending', label: 'F/U Pending', color: '#f97316', bg: '#ffedd5' },
  { key: 'followup-complete', label: 'F/U Complete', color: '#2563eb', bg: '#dbeafe' },
];

const TAG_MAP = {
  sent:      { label: 'F/U Sent',     color: '#d97706', bg: '#fef3c7' },
  opened:    { label: 'F/U Opened',   color: '#0891b2', bg: '#cffafe' },
  pending:   { label: 'F/U Pending',  color: '#f97316', bg: '#ffedd5' },
  completed: { label: 'F/U Complete', color: '#2563eb', bg: '#dbeafe' },
};

function getTypeTag(patient) {
  const fs = patient.followup_status;
  const ver = patient.follow_up_count || 0;
  if (fs && TAG_MAP[fs]) {
    const t = TAG_MAP[fs];
    return { ...t, label: `${t.label} (V${ver})` };
  }
  if (patient.status === 'pending') return { label: 'Intake Pending', color: '#f59e0b', bg: '#fef3c7' };
  return { label: 'Intake Complete', color: '#16a34a', bg: '#dcfce7' };
}

export default function PatientsList({ initialFilter = 'all', dateRange: dateRangeProp, onFilterUsed, onClearDateRange }) {
  const { tenant } = useTenant();
  const primaryColor = tenant?.primaryColor || '#06AB8B';
  const tenantId = tenant?.id;
  // DISABLED: Pain Diagram step hidden — form shows 12 modules only
  // const hasPainDiagram = tenant?.features?.pain_diagram !== false;
  const hasPainDiagram = false;

  const [filterKey, setFilterKey] = useState('all');
  const [activeDateRange, setActiveDateRange] = useState(dateRangeProp);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, 'intake-pending': 0, 'intake-complete': 0, 'followup-sent': 0, 'followup-pending': 0, 'followup-complete': 0 });
  const [sortBy, setSortBy] = useState('updated');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stepNames, setStepNames] = useState([]);
  const [moduleFieldMap, setModuleFieldMap] = useState({});
  const [accordionLabelMap, setAccordionLabelMap] = useState({});
  const [fieldLabelMap, setFieldLabelMap] = useState({});

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Follow-up expanded rows & version history
  const [expandedRow, setExpandedRow] = useState(null);
  const [versionHistory, setVersionHistory] = useState({});
  const [loadingVersions, setLoadingVersions] = useState({});
  const [versionDialog, setVersionDialog] = useState({ open: false, data: null });
  const [loadingVersionData, setLoadingVersionData] = useState(false);

  // Delete contact
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, email: '', name: '' });
  const [deleting, setDeleting] = useState(false);

  // On-demand intake PDF regeneration
  const [generatingPdfFor, setGeneratingPdfFor] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Manual sync to GHL (preview, confirm overwrites, push)
  const [ghlSyncPatient, setGhlSyncPatient] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      let url = `${environment.baseUrl}/api/admin/submissions?tenantId=${tenantId}&page=${page + 1}&limit=${rowsPerPage}&filter=${filterKey}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
      if (activeDateRange?.range) {
        url += `&dateRange=${activeDateRange.range}`;
        if (activeDateRange.range === 'custom' && activeDateRange.startDate && activeDateRange.endDate) {
          url += `&startDate=${activeDateRange.startDate}&endDate=${activeDateRange.endDate}`;
        }
      }
      const res = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch submissions');
      const data = await res.json();
      setSubmissions(data.submissions || []);
      setTotal(data.pagination?.total || 0);
      if (data.counts) setCounts(data.counts);
      if (data.stepNames?.length) setStepNames(data.stepNames);
      if (data.moduleFieldMap) setModuleFieldMap(data.moduleFieldMap);
      if (data.accordionLabelMap) setAccordionLabelMap(data.accordionLabelMap);
      if (data.fieldLabelMap) setFieldLabelMap(data.fieldLabelMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, rowsPerPage, filterKey, debouncedSearch, activeDateRange, sortBy, sortOrder]);

  useEffect(() => {
    if (tenantId) fetchSubmissions();
  }, [tenantId, fetchSubmissions]);

  useEffect(() => {
    if (initialFilter && initialFilter !== 'all') {
      setFilterKey(initialFilter);
      onFilterUsed?.();
    }
  }, [initialFilter, onFilterUsed]);

  useEffect(() => {
    setActiveDateRange(dateRangeProp);
  }, [dateRangeProp]);

  const handleFilterChange = (key) => {
    setFilterKey(key);
    setPage(0);
    setSearch('');
    setDebouncedSearch('');
    setExpandedRow(null);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val.trim());
      setPage(0);
    }, 400);
  };

  // --- Actions ---
  const handleDeactivate = async (patient) => {
    if (!window.confirm(`Deactivate ${patient.patient_email}?`)) return;
    try {
      await fetch(`${environment.baseUrl}/api/admin/contacts/deactivate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}` },
        body: JSON.stringify({ email: patient.patient_email, tenantId }),
      });
      fetchSubmissions();
    } catch (err) {
      alert('Failed to deactivate');
    }
  };

  const handleActivate = async (patient) => {
    try {
      await fetch(`${environment.baseUrl}/api/admin/contacts/activate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}` },
        body: JSON.stringify({ email: patient.patient_email, tenantId }),
      });
      fetchSubmissions();
    } catch (err) {
      alert('Failed to activate');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.email) return;
    setDeleting(true);
    try {
      const res = await fetch(`${environment.baseUrl}/api/admin/contacts/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('superAdminToken')}` },
        body: JSON.stringify({ email: deleteConfirm.email, tenantId }),
      });
      const data = await res.json();
      if (data.success) {
        setDeleteConfirm({ open: false, email: '', name: '' });
        fetchSubmissions();
      } else {
        setError(data.message || 'Failed to delete');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // --- Intake PDF regeneration ---
  // Rebuilds the assessment-form PDF from stored form data using the same generator
  // the intake client runs, for patients whose PDF never made it to Drive.
  const handleGeneratePdf = async (patient) => {
    setPdfError(null);
    setGeneratingPdfFor(patient.submission_id);
    try {
      await downloadPatientPdf({ submission: patient, tenantLogoUrl: tenant?.logoUrl });
    } catch (err) {
      logger.error('Error generating patient PDF:', err);
      setPdfError(
        `Could not generate a PDF for ${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim() +
        `: ${err.message}`
      );
    } finally {
      setGeneratingPdfFor(null);
    }
  };

  // --- Follow-up version history ---
  const handleExpandRow = (submission) => {
    const sid = submission.submission_id;
    if (expandedRow === sid) {
      setExpandedRow(null);
    } else {
      setExpandedRow(sid);
      fetchVersionHistory(sid, submission.access_token);
    }
  };

  const fetchVersionHistory = async (submissionId, accessToken) => {
    if (versionHistory[submissionId]) return;
    setLoadingVersions((prev) => ({ ...prev, [submissionId]: true }));
    try {
      const res = await fetch(`${environment.baseUrl}/api/followup/history/${submissionId}`, {
        credentials: 'include',
        headers: { 'X-Access-Token': accessToken },
      });
      if (res.ok) {
        const data = await res.json();
        setVersionHistory((prev) => ({ ...prev, [submissionId]: data.history || [] }));
      }
    } catch (err) {
      logger.error('Error fetching version history:', err);
    } finally {
      setLoadingVersions((prev) => ({ ...prev, [submissionId]: false }));
    }
  };

  const fetchVersionData = async (submissionId, version, accessToken) => {
    setLoadingVersionData(true);
    try {
      const res = await fetch(`${environment.baseUrl}/api/followup/version/${submissionId}/${version}`, {
        credentials: 'include',
        headers: { 'X-Access-Token': accessToken },
      });
      if (res.ok) {
        const data = await res.json();
        setVersionDialog({ open: true, data: data.version });
      }
    } catch (err) {
      logger.error('Error fetching version data:', err);
    } finally {
      setLoadingVersionData(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const isFollowUpTab = filterKey === 'followup-complete' || filterKey === 'followup-pending';

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700} color="#333">
          Patients
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
            }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSubmissions} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {pdfError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>{pdfError}</Alert>}

      {activeDateRange && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Filtered: ${activeDateRange.range === 'today' ? 'Today' : activeDateRange.range === 'weekly' ? 'This Week' : activeDateRange.range === 'monthly' ? 'This Month' : activeDateRange.range === 'custom' ? `${new Date(activeDateRange.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} to ${new Date(activeDateRange.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}` : activeDateRange.range}`}
            onDelete={() => { setActiveDateRange(null); onClearDateRange?.(); }}
            sx={{ fontWeight: 600, bgcolor: primaryColor, color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff', '&:hover': { color: '#ddd' } } }}
          />
        </Box>
      )}

      {/* Filter Chips */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
          {FILTER_CHIPS.map((chip) => {
            const isActive = filterKey === chip.key;
            const count = counts[chip.key] ?? 0;
            return (
              <Chip
                key={chip.key}
                label={`${chip.label} (${count})`}
                onClick={() => handleFilterChange(chip.key)}
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  backgroundColor: isActive ? (chip.bg || primaryColor) : '#f5f5f5',
                  color: isActive ? (chip.color || '#fff') : '#666',
                  border: isActive ? `1.5px solid ${chip.color || primaryColor}` : '1.5px solid transparent',
                  '&:hover': { backgroundColor: chip.bg || '#e0e0e0' },
                }}
              />
            );
          })}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafb' }}>
                {isFollowUpTab && <TableCell width={50} />}
                <SortableHeader label="Name" column="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Email" column="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <SortableHeader label={isFollowUpTab ? 'Follow-ups' : 'Submissions'} column="submissions" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} align="center" />
                <SortableHeader label="Status" column="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Created" column="created" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Last Updated" column="updated" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isFollowUpTab ? 9 : 8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} sx={{ color: primaryColor }} />
                  </TableCell>
                </TableRow>
              ) : !tenantId ? (
                <TableRow>
                  <TableCell colSpan={isFollowUpTab ? 9 : 8} align="center" sx={{ py: 6, color: '#999' }}>
                    No tenant configured
                  </TableCell>
                </TableRow>
              ) : submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isFollowUpTab ? 9 : 8} align="center" sx={{ py: 6, color: '#999' }}>
                    {debouncedSearch ? `No patients found for "${debouncedSearch}"` : 'No patients found'}
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((p) => (
                  <PatientRow
                    key={p.submission_id}
                    patient={p}
                    isFollowUpTab={isFollowUpTab}
                    primaryColor={primaryColor}
                    totalSteps={stepNames.length + (hasPainDiagram ? 1 : 0)}
                    expandedRow={expandedRow}
                    versionHistory={versionHistory}
                    loadingVersions={loadingVersions}
                    onExpand={() => handleExpandRow(p)}
                    onViewDetail={() => { setSelectedPatient(p); setDetailOpen(true); }}
                    onGeneratePdf={() => handleGeneratePdf(p)}
                    generatingPdf={generatingPdfFor === p.submission_id}
                    onSyncGhl={() => setGhlSyncPatient(p)}
                    onDeactivate={() => handleDeactivate(p)}
                    onActivate={() => handleActivate(p)}
                    onDelete={() => setDeleteConfirm({ open: true, email: p.patient_email, name: `${p.patient_last_name}, ${p.patient_first_name}` })}
                    onViewVersion={(ver) => fetchVersionData(p.submission_id, ver.version, p.access_token)}
                    formatDate={formatDate}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Patient Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Patient Details
          <IconButton size="small" onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pdfError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>{pdfError}</Alert>}
          {selectedPatient && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedPatient.status === 'pending' ? (
                <PendingPatientDetail patient={selectedPatient} formatDate={formatDate} stepNames={stepNames} moduleFieldMap={moduleFieldMap} accordionLabelMap={accordionLabelMap} fieldLabelMap={fieldLabelMap} hasPainDiagram={hasPainDiagram} />
              ) : (
                <CompletedPatientDetail patient={selectedPatient} formatDate={formatDate} primaryColor={primaryColor} moduleFieldMap={moduleFieldMap} stepNames={stepNames} accordionLabelMap={accordionLabelMap} fieldLabelMap={fieldLabelMap} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPatient && (
            <Button
              variant="outlined"
              startIcon={
                generatingPdfFor === selectedPatient.submission_id
                  ? <CircularProgress size={16} sx={{ color: primaryColor }} />
                  : <PictureAsPdfIcon />
              }
              disabled={generatingPdfFor === selectedPatient.submission_id}
              onClick={() => handleGeneratePdf(selectedPatient)}
              sx={{ borderColor: primaryColor, color: primaryColor, textTransform: 'none', fontWeight: 600, mr: 'auto' }}
            >
              Generate Intake PDF
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Version Data Dialog */}
      <Dialog open={versionDialog.open} onClose={() => setVersionDialog({ open: false, data: null })} maxWidth="md" fullWidth>
        <DialogTitle>Version {versionDialog.data?.version} - Form Data</DialogTitle>
        <DialogContent dividers>
          {loadingVersionData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : versionDialog.data ? (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Created: {formatDate(versionDialog.data.createdAt)}
              </Typography>
              {versionDialog.data.selectedModules?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Modules Updated:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {versionDialog.data.selectedModules.map((mod, i) => <Chip key={i} label={mod} size="small" />)}
                  </Box>
                </Box>
              )}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Form Data:</Typography>
              <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', backgroundColor: '#f5f5f5' }}>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(versionDialog.data.formData, null, 2)}
                </pre>
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialog({ open: false, data: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <GhlSyncDialog
        open={!!ghlSyncPatient}
        patient={ghlSyncPatient}
        tenantId={tenantId}
        primaryColor={primaryColor}
        onClose={(changed) => { setGhlSyncPatient(null); if (changed) fetchSubmissions(); }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => !deleting && setDeleteConfirm({ open: false, email: '', name: '' })}>
        <DialogTitle sx={{ color: '#d32f2f' }}>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email})?
          </Typography>
          <Box component="ul" sx={{ mt: 1, color: '#666', fontSize: 13, pl: 2 }}>
            <li>All submissions from the database</li>
            <li>All follow-up version history</li>
            <li>Contact from GHL (if configured)</li>
            <li>OTP records</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>This action cannot be undone.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, email: '', name: '' })} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PatientRow({
  patient: p, isFollowUpTab, primaryColor, totalSteps,
  expandedRow, versionHistory, loadingVersions,
  onExpand, onViewDetail, onGeneratePdf, generatingPdf, onSyncGhl, onDeactivate, onActivate, onDelete, onViewVersion,
  formatDate,
}) {
  const isExpanded = expandedRow === p.submission_id;
  const tag = getTypeTag(p);
  const hasFollowup = !!p.followup_status;

  return (
    <>
      <TableRow hover sx={{ cursor: hasFollowup ? 'pointer' : 'default', backgroundColor: isExpanded ? '#f0f7ff' : 'inherit' }}
        onClick={hasFollowup ? onExpand : undefined}
      >
        {isFollowUpTab && (
          <TableCell>
            {hasFollowup ? (
              <IconButton size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            ) : null}
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {p.patient_last_name}, {p.patient_first_name}
          </Typography>
        </TableCell>
        <TableCell sx={{ color: '#555', fontSize: 13 }}>{p.patient_email}</TableCell>
        <TableCell>
          <Chip
            label={tag.label}
            size="small"
            sx={{ backgroundColor: tag.bg, color: tag.color, fontWeight: 600, fontSize: 11 }}
          />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={isFollowUpTab ? (p.follow_up_count || 0) : (p.total_submissions || 1)}
            size="small"
            sx={{
              backgroundColor: isFollowUpTab
                ? ((p.follow_up_count || 0) > 0 ? '#e3f2fd' : '#f5f5f5')
                : '#e3f2fd',
              color: isFollowUpTab
                ? ((p.follow_up_count || 0) > 0 ? '#1976d2' : '#999')
                : '#1976d2',
              fontWeight: 600,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            label={p.status === 'pending' ? `Pending — Step ${p.last_completed_step || 1}/${totalSteps || '?'}` : p.is_active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              backgroundColor: p.status === 'pending' ? '#FEF3E2' : p.is_active ? '#e8f5e9' : '#ffebee',
              color: p.status === 'pending' ? '#F59E0B' : p.is_active ? '#2e7d32' : '#c62828',
              fontWeight: 500,
            }}
          />
        </TableCell>
        <TableCell sx={{ color: '#666', fontSize: 13 }}>{formatDate(p.created_at)}</TableCell>
        <TableCell sx={{ color: '#666', fontSize: 13 }}>{formatDate(p.updated_at)}</TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={onViewDetail}>
                <VisibilityIcon fontSize="small" sx={{ color: primaryColor }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Generate Intake PDF">
              <span>
                <IconButton size="small" onClick={onGeneratePdf} disabled={generatingPdf}>
                  {generatingPdf
                    ? <CircularProgress size={18} sx={{ color: primaryColor }} />
                    : <PictureAsPdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Sync to GHL">
              <IconButton size="small" onClick={onSyncGhl}>
                <CloudSyncIcon fontSize="small" sx={{ color: '#1976d2' }} />
              </IconButton>
            </Tooltip>
            {p.is_active ? (
              <Tooltip title="Deactivate">
                <IconButton size="small" onClick={onDeactivate}>
                  <PersonOffIcon fontSize="small" sx={{ color: '#FF9800' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Activate">
                <IconButton size="small" onClick={onActivate}>
                  <PersonIcon fontSize="small" sx={{ color: '#4CAF50' }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete}>
                <DeleteIcon fontSize="small" sx={{ color: '#ef5350' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable version history (any patient with followup) */}
      {hasFollowup && (
        <TableRow>
          <TableCell colSpan={isFollowUpTab ? 9 : 8} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 3, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Version History for {p.patient_last_name}, {p.patient_first_name}
                </Typography>
                {loadingVersions[p.submission_id] ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading versions...</Typography>
                  </Box>
                ) : (versionHistory[p.submission_id]?.length || 0) > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Modules Updated</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {versionHistory[p.submission_id].map((v) => (
                        <TableRow key={v.version}>
                          <TableCell>
                            <Chip label={`V${v.version}`} color={v.version === 0 ? 'success' : 'primary'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={v.type === 'original' ? 'Intake' : 'Follow-up'}
                              color={v.type === 'original' ? 'success' : 'info'}
                              size="small" variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label="Complete" size="small" sx={{ backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: 11 }} />
                          </TableCell>
                          <TableCell>
                            {v.selectedModules?.length > 0 ? v.selectedModules.join(', ') : v.type === 'original' ? 'All Modules' : '-'}
                          </TableCell>
                          <TableCell>{formatDate(v.createdAt)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {v.pdfUrl && (
                                <Tooltip title="View PDF">
                                  <IconButton size="small" href={v.pdfUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
                                    <PictureAsPdfIcon fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {v.hasFormData && (
                                <Tooltip title="View Form Data">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onViewVersion(v); }}>
                                    <CompareArrowsIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Pending follow-up row — shows when a new follow-up is sent but not yet submitted */}
                      {p.followup_status && p.followup_status !== 'completed' && (
                        <TableRow sx={{ backgroundColor: '#fffbeb' }}>
                          <TableCell>
                            <Chip label={`V${(p.follow_up_count || 0) + 1}`} size="small" variant="outlined" sx={{ borderColor: '#f59e0b', color: '#f59e0b' }} />
                          </TableCell>
                          <TableCell>
                            <Chip label="Follow-up" color="info" size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={TAG_MAP[p.followup_status]?.label || p.followup_status}
                              size="small"
                              sx={{ backgroundColor: TAG_MAP[p.followup_status]?.bg || '#fef3c7', color: TAG_MAP[p.followup_status]?.color || '#f59e0b', fontWeight: 600, fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#999', fontStyle: 'italic' }}>Waiting for patient</TableCell>
                          <TableCell>{p.followup_sent_at ? formatDate(p.followup_sent_at) : '-'}</TableCell>
                          <TableCell>-</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    No version history available. Version tracking starts from next follow-up.
                  </Typography>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const FORM_FIELD_LABELS = {
  firstname: 'First Name', lastname: 'Last Name', email: 'Email', phone: 'Phone',
  dateofbirth: 'Date of Birth', gender: 'Gender', maritalstatus: 'Marital Status',
  addressstreet: 'Street Address', addresscity: 'City', addressstate: 'State',
  addresscountry: 'Country', postalcode: 'Postal Code',
  employment: 'Employment', father: 'Father', mother: 'Mother',
  smoking: 'Smoking', alcohol: 'Alcohol', illicitdruguse: 'Illicit Drug Use',
  laterality: 'Laterality', newissues: 'New Issues', hasnewissues: 'Has New Issues',
  hasNewIssues: 'Has New Issues', currentlylivingwith: 'Currently Living With',
  painLevel_best: 'Best Pain Level', painLevel_worst: 'Worst Pain Level',
  painLevel_current: 'Current Pain Level',
  bestpainlevel: 'Best Pain Level', worstpainlevel: 'Worst Pain Level',
  currentpainlevel: 'Current Pain Level', primaryareaofconcern: 'Primary Area of Concern',
  medicationcount: 'Medication Count', newissuestimestamp: 'New Issues Timestamp',
  newIssuesDescription: 'New Issues Description',
  howdidyourcurrentproblemstart: 'How Did Your Current Problem Start',
  describethetimingofyoursymptoms: 'Describe the Timing of Your Symptoms',
  activitiesthatmaketheproblemworse: 'Activities That Make the Problem Worse',
  activitiesthatmaketheproblembetter: 'Activities That Make the Problem Better',
  howlonghaveyouexperiencedtheproblem: 'How Long Have You Experienced the Problem',
  howlongcanyoustandwithouttheneedtomove: 'How Long Can You Stand Without Need to Move',
  howlongcanyousitinoneplacewithouttheneedtomove: 'How Long Can You Sit Without Need to Move',
  overthelastweekhowhaveyoursymptomsprogressed: 'How Have Your Symptoms Progressed (Last Week)',
  overthelastmonthhowhaveyoursymptomsprogressed: 'How Have Your Symptoms Progressed (Last Month)',
  describethefrequencyofyoursymptomsorhowoftentheyoccur: 'Frequency of Symptoms',
  inyourownwordspleasebrieflydescribeyourmainissueorcomplaint: 'Main Issue / Complaint',
  familyhistoryofsurgicalhistory: 'Family History — Surgical',
  familyhistoryofdegenerativedisorders: 'Family History — Degenerative Disorders',
  familyhistoryofautoimmuneinflammatorydisorders: 'Family History — Autoimmune/Inflammatory',
  authorizedcontactscount: 'Authorized Contacts Count',
  authorizedcontact1firstname: 'Authorized Contact 1 — First Name',
  authorizedcontact1lastname: 'Authorized Contact 1 — Last Name',
  authorizedcontact1phone: 'Authorized Contact 1 — Phone',
  authorizedcontact1email: 'Authorized Contact 1 — Email',
  authorizedcontact1relationship: 'Authorized Contact 1 — Relationship',
  authorizedcontact1isemergency: 'Authorized Contact 1 — Emergency Contact',
  authorizedcontact1ismedicalauth: 'Authorized Contact 1 — Medical Authorization',
  authorizedcontact2firstname: 'Authorized Contact 2 — First Name',
  authorizedcontact2lastname: 'Authorized Contact 2 — Last Name',
  authorizedcontact2phone: 'Authorized Contact 2 — Phone',
  authorizedcontact2email: 'Authorized Contact 2 — Email',
  authorizedcontact2relationship: 'Authorized Contact 2 — Relationship',
  authorizedcontact2isemergency: 'Authorized Contact 2 — Emergency Contact',
  authorizedcontact2ismedicalauth: 'Authorized Contact 2 — Medical Authorization',
  medicationwizard: 'Medications (Wizard)',
  manualmedicationlist: 'Medications (Manual)',
  medicationlistfromfiles: 'Medications (From Files)',
  medicationlistfromcamera: 'Medications (From Camera)',
  medicationvoiceinputlist: 'Medications (Voice Input)',
  manualallergieslist: 'Allergies (Manual)',
  allergieslistfromfiles: 'Allergies (From Files)',
  allergiesvoiceinputlist: 'Allergies (Voice Input)',
  pharmacyformatted: 'Pharmacy',
};

const SKIP_FIELDS = [
  'tc', 'tenant_id', 'totalImages', 'pharmacydata', 'allergiesdata', 'medicationdata',
  'painDiagramData', 'manualAllergies', 'manualMedications',
  '_lastFollowUpDate', '_lastFollowUpModules',
];

const MODULE_AUX_KEYS = {
  medication: ['manualmedicationlist', 'medicationlistfromfiles', 'medicationlistfromcamera', 'medicationvoiceinputlist'],
  allergies: ['manualallergieslist', 'allergieslistfromfiles', 'allergiesvoiceinputlist'],
  pharmacy: ['pharmacyformatted'],
};

function getModuleTypeTag(moduleName) {
  const n = moduleName.toLowerCase();
  if (n.includes('medication') && n.includes('allerg')) return 'allergies';
  if (n.includes('medication')) return 'medication';
  if (n.includes('allerg')) return 'allergies';
  if (n.includes('pharmacy')) return 'pharmacy';
  return null;
}

function derivePharmacyFormatted(fd) {
  if (fd.pharmacyformatted) return;
  const sp = fd.pharmacydata?.selectedPharmacy;
  if (!sp) return;
  const name = sp.pharmacy_name || sp.name || '';
  if (!name) return;
  const addr = sp.shop_address || sp.address || '';
  const city = sp.city || '';
  const zip = sp.zip_code || sp.zip || '';
  const phone = sp.contact_number || sp.phone || '';
  fd.pharmacyformatted = `${name}\n${addr} ${city} ${zip}\n${phone}`.trim();
}

function collapseAccordionRows(rows, accordionLabelMap) {
  if (!accordionLabelMap || !Object.keys(accordionLabelMap).length) return rows;
  const accRows = [];
  const otherRows = [];
  rows.forEach(([k, v]) => {
    if (/^accordionsection\d+checkbox\d+/.test(k) && accordionLabelMap[k]) accRows.push([k, v]);
    else otherRows.push([k, v]);
  });
  if (!accRows.length) return rows;
  const sections = {};
  accRows.forEach(([k]) => {
    const info = accordionLabelMap[k];
    const secTitle = info.section;
    if (!sections[secTitle]) sections[secTitle] = [];
    sections[secTitle].push(info.label);
  });
  const collapsed = Object.entries(sections).map(([title, labels]) => [title, labels.join(', ')]);
  return [...otherRows, ...collapsed];
}

function groupFormDataByModule(fd, stepNames, moduleFieldMap, isVisible, maxStep, accordionLabelMap, fieldLabelMap) {
  derivePharmacyFormatted(fd);
  const groupedModules = [];
  const assignedFields = new Set();

  // Collect ALL known field names across ALL modules (including beyond maxStep)
  // so they don't leak into "ungrouped"
  stepNames.forEach((moduleName) => {
    const fieldNames = moduleFieldMap[moduleName] || [];
    fieldNames.forEach(fn => {
      assignedFields.add(fn);
      if (fn.length >= 3) {
        Object.keys(fd).forEach(fdKey => {
          if (fdKey !== fn && fdKey.startsWith(fn)) assignedFields.add(fdKey);
        });
      }
    });
    const tag = getModuleTypeTag(moduleName);
    if (tag && MODULE_AUX_KEYS[tag]) {
      MODULE_AUX_KEYS[tag].forEach(k => assignedFields.add(k));
    }
  });

  // Build grouped rows only for completed steps
  stepNames.forEach((moduleName, i) => {
    if (maxStep !== undefined && i + 1 > maxStep) return;
    const fieldNames = moduleFieldMap[moduleName] || [];
    const rows = [];

    fieldNames.forEach(fn => {
      if (fd[fn] !== undefined && isVisible(fn, fd[fn])) {
        rows.push([fn, fd[fn]]);
      }
      if (fn.length >= 3) {
        Object.keys(fd).forEach(fdKey => {
          if (fdKey !== fn && fdKey.startsWith(fn) && fd[fdKey] !== undefined && isVisible(fdKey, fd[fdKey])) {
            if (!rows.some(([k]) => k === fdKey)) rows.push([fdKey, fd[fdKey]]);
          }
        });
      }
    });

    const tag = getModuleTypeTag(moduleName);
    if (tag && MODULE_AUX_KEYS[tag]) {
      MODULE_AUX_KEYS[tag].forEach(auxKey => {
        if (fd[auxKey] !== undefined && isVisible(auxKey, fd[auxKey]) && !rows.some(([k]) => k === auxKey)) {
          rows.push([auxKey, fd[auxKey]]);
        }
      });
    }

    if (rows.length > 0) {
      groupedModules.push({ name: moduleName, stepNum: i + 1, rows: collapseAccordionRows(rows, accordionLabelMap) });
    }
  });

  const ungrouped = Object.entries(fd).filter(([k, v]) =>
    !assignedFields.has(k) && isVisible(k, v)
  );

  return { groupedModules, ungrouped };
}

function formatFieldName(key, fieldLabelMap) {
  if (FORM_FIELD_LABELS[key]) return FORM_FIELD_LABELS[key];
  if (fieldLabelMap && fieldLabelMap[key]) return fieldLabelMap[key];
  if (key.includes('conditional')) {
    const base = key.replace(/conditional\d+$/, '');
    return FORM_FIELD_LABELS[base] || (fieldLabelMap && fieldLabelMap[base]) || formatFieldName(base) + ' (Details)';
  }
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

function formatAuthorizedContacts(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) return '-';
  return contacts.map(c => {
    const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unknown';
    const rel = c.relationship || '';
    const tags = [];
    if (c.isEmergencyContact) tags.push('Emergency');
    if (c.isAuthorizedMedical) tags.push('Medical');
    const line1 = `${name}${rel ? ' — ' + rel : ''}${tags.length ? ' (' + tags.join(', ') + ')' : ''}`;
    const line2 = [c.email, c.phone].filter(Boolean).join(' | ');
    return line2 ? `${line1}\n${line2}` : line1;
  }).join('\n\n');
}

function formatFieldValue(val, key) {
  if (val === null || val === undefined || val === '') return '-';
  if (key === 'authorizedcontacts') return formatAuthorizedContacts(val);
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
    try { return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { /* fall through */ }
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return '-';
    const nonEmpty = val.filter(v => {
      if (typeof v !== 'object' || !v) return true;
      return Object.entries(v).filter(([k]) => k !== 'id').some(([, x]) => x !== '' && x !== false && x !== 0 && x !== null && x !== undefined);
    });
    if (nonEmpty.length === 0) return '-';
    return nonEmpty.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ');
  }
  if (typeof val === 'object') {
    const entries = Object.entries(val).filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== false);
    if (entries.length === 0) return '-';
    return entries.map(([k, v]) => {
      const label = k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
      return `${label}: ${typeof v === 'object' ? JSON.stringify(v) : v}`;
    }).join('; ');
  }
  const str = String(val);
  if (str === 'true') return 'Yes';
  if (str === 'false') return 'No';
  return str;
}

function CompletedPatientDetail({ patient, formatDate, primaryColor, moduleFieldMap = {}, stepNames = [], accordionLabelMap = {}, fieldLabelMap = {} }) {
  const fd = typeof patient.form_data === 'string'
    ? (() => { try { return JSON.parse(patient.form_data); } catch { return {}; } })()
    : (patient.form_data || {});

  const PATIENT_INFO_KEYS = new Set(['firstname', 'lastname', 'email', 'phone', 'postalcode']);
  const HIDDEN_KEYS = new Set([
    'tc', 'tenant_id', 'totalImages', 'painDiagramData',
    '_lastFollowUpDate', '_lastFollowUpModules',
  ]);

  const isVisible = (k, v) => {
    if (PATIENT_INFO_KEYS.has(k.toLowerCase())) return false;
    if (HIDDEN_KEYS.has(k)) return false;
    if (v === null || v === undefined || v === '' || v === false) return false;
    if (typeof v === 'string' && v.startsWith('data:image/')) return false;
    return true;
  };

  const { groupedModules, ungrouped } = groupFormDataByModule(fd, stepNames, moduleFieldMap, isVisible, undefined, accordionLabelMap, fieldLabelMap);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip
          label={patient.is_active ? 'Active' : 'Inactive'}
          size="small"
          sx={{ backgroundColor: patient.is_active ? '#e8f5e9' : '#ffebee', color: patient.is_active ? '#2e7d32' : '#c62828', fontWeight: 600 }}
        />
        {patient.merged_pdf_url && (
          <Button
            variant="outlined"
            size="small"
            href={patient.merged_pdf_url}
            target="_blank"
            startIcon={<PictureAsPdfIcon />}
            sx={{ borderColor: primaryColor, color: primaryColor, textTransform: 'none', fontWeight: 600 }}
          >
            View PDF
          </Button>
        )}
      </Box>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>Patient Info</Typography>
      <DetailRow label="Name" value={`${patient.patient_last_name || ''}, ${patient.patient_first_name || ''}`} />
      <DetailRow label="Email" value={patient.patient_email} />
      <DetailRow label="Total Submissions" value={patient.total_submissions || 1} />
      <DetailRow label="Follow-ups" value={patient.follow_up_count || 0} />
      <DetailRow label="Created" value={formatDate(patient.created_at)} />
      <DetailRow label="Last Updated" value={formatDate(patient.updated_at)} />

      {groupedModules.map(({ name, stepNum, rows }) => (
        <Box key={name} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Step {stepNum}: {name}
          </Typography>
          {rows.map(([k, v]) => (
            <DetailRow key={k} label={formatFieldName(k, fieldLabelMap)} value={formatFieldValue(v, k)} />
          ))}
        </Box>
      ))}

      {ungrouped.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Other Data</Typography>
          {ungrouped.map(([k, v]) => (
            <DetailRow key={k} label={formatFieldName(k, fieldLabelMap)} value={formatFieldValue(v, k)} />
          ))}
        </Box>
      )}
    </>
  );
}

function PendingPatientDetail({ patient, formatDate, stepNames: apiStepNames, moduleFieldMap = {}, accordionLabelMap = {}, fieldLabelMap = {}, hasPainDiagram = true }) {
  const fd = typeof patient.form_data === 'string'
    ? (() => { try { return JSON.parse(patient.form_data); } catch { return {}; } })()
    : (patient.form_data || {});

  const lastStep = patient.last_completed_step || 1;
  const STEP_NAMES = apiStepNames?.length > 0
    ? apiStepNames
    : ['Step 1', 'Step 2', 'Step 3', 'Step 4', 'Step 5'];
  const totalSteps = STEP_NAMES.length + (hasPainDiagram ? 1 : 0);

  const PATIENT_INFO_KEYS = new Set(['firstname', 'lastname', 'email', 'phone', 'postalcode']);
  const HIDDEN_KEYS = new Set([
    'tc', 'tenant_id', 'totalImages', 'painDiagramData',
    '_lastFollowUpDate', '_lastFollowUpModules',
    'pharmacydata', 'allergiesdata', 'medicationdata',
    'manualAllergies', 'manualMedications',
    'medicationlistfromwizard',
  ]);

  const isEmptyValue = (v) => {
    if (v === null || v === undefined || v === '' || v === '-' || v === false) return true;
    if (typeof v === 'string' && v.startsWith('data:image/')) return true;
    if (Array.isArray(v)) {
      if (v.length === 0) return true;
      if (v.every(item => typeof item === 'object' && item &&
        Object.entries(item).filter(([k]) => k !== 'id').every(([, x]) => x === '' || x === false || x === 0 || x === null || x === undefined)
      )) return true;
    }
    if (typeof v === 'object' && !Array.isArray(v) && v) {
      if (Object.values(v).every(x => x === 0 || x === '' || x === null || x === false)) return true;
    }
    return false;
  };

  const isFieldVisible = (k, v) => {
    if (PATIENT_INFO_KEYS.has(k.toLowerCase())) return false;
    if (HIDDEN_KEYS.has(k)) return false;
    if (isEmptyValue(v)) return false;
    if (k.toLowerCase().includes('count') && v === 0) return false;
    return true;
  };

  const { groupedModules, ungrouped } = groupFormDataByModule(fd, STEP_NAMES, moduleFieldMap, isFieldVisible, lastStep, accordionLabelMap, fieldLabelMap);

  return (
    <>
      <Chip label={`Pending — Step ${lastStep} of ${totalSteps} completed`} size="small"
        sx={{ backgroundColor: '#FEF3E2', color: '#F59E0B', fontWeight: 600, alignSelf: 'flex-start' }} />

      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, mb: 1 }}>
        {STEP_NAMES.map((name, i) => (
          <Tooltip key={name} title={`${name}${i + 1 <= lastStep ? ' ✓' : ''}`}>
            <Box sx={{
              flex: 1, height: 6, borderRadius: 3,
              backgroundColor: i + 1 <= lastStep ? 'var(--color-primary, #06AB8B)' : '#E5E7EB',
            }} />
          </Tooltip>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Last updated: {formatDate(patient.updated_at)}
      </Typography>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1.5 }}>Patient Info</Typography>
      <DetailRow label="First Name" value={patient.patient_first_name || '-'} />
      <DetailRow label="Last Name" value={patient.patient_last_name || '-'} />
      <DetailRow label="Email" value={patient.patient_email || '-'} />
      <DetailRow label="Phone" value={fd.phone || patient.patient_phone || '-'} />
      <DetailRow label="Postal Code" value={fd.postalcode || '-'} />

      {groupedModules.map(({ name, stepNum, rows }) => (
        <Box key={name} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Step {stepNum}: {name}
          </Typography>
          {rows.map(([k, v]) => (
            <DetailRow key={k} label={formatFieldName(k, fieldLabelMap)} value={formatFieldValue(v, k)} />
          ))}
        </Box>
      ))}

      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Steps Progress</Typography>
      {STEP_NAMES.map((name, i) => (
        <DetailRow key={name} label={`Step ${i + 1}: ${name}`} value={i + 1 <= lastStep ? '✓ Completed' : '—'} />
      ))}
      {hasPainDiagram && (
        <DetailRow label={`Step ${STEP_NAMES.length + 1}: Pain Diagram`} value={patient.status === 'completed' ? '✓ Completed' : '—'} />
      )}
    </>
  );
}

function SortableHeader({ label, column, sortBy, sortOrder, onSort, align }) {
  const isActive = sortBy === column;
  return (
    <TableCell
      align={align}
      sx={{ fontWeight: 600, cursor: 'pointer', userSelect: 'none', '&:hover': { backgroundColor: '#eef1f5' } }}
      onClick={() => onSort(column)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        {isActive ? (
          sortOrder === 'asc'
            ? <ArrowUpwardIcon sx={{ fontSize: 16, color: '#1976d2' }} />
            : <ArrowDownwardIcon sx={{ fontSize: 16, color: '#1976d2' }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14, color: '#ccc' }} />
        )}
      </Box>
    </TableCell>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, borderBottom: '1px solid #f0f0f0', pb: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180, maxWidth: 180, fontWeight: 500, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{value || '-'}</Typography>
    </Box>
  );
}
